import { api } from '../api.js';
import { subscribe } from '../state.js';
import { navigate } from '../router.js';
import { getTranslatedFields, translate } from '../lib/i18n.js';
import { createFlowRenderer } from '../components/flowRenderer.js';
import type { ApiConceptDetail, ApiConceptListItem } from '../../shared/types.js';

interface FlowStep {
  id: number;
  label: string;
  shape: string;
  sort_order: number;
  conceptLinks?: { slug: string; title: string }[];
}

interface FlowEdge {
  id: number;
  from_step_id: number;
  to_step_id: number;
  label?: string;
}

interface FlowData {
  steps: FlowStep[];
  edges: FlowEdge[];
}

/**
 * Convert [[slug]] in text to clickable HTML links.
 * Uses conceptMap to show titles instead of slugs when available.
 */
function renderInlineLinks(text: string, conceptMap: Map<string, string>): string {
  return text.replace(/\[\[([^\]]+)\]\]/g, (_: string, slug: string) => {
    const display = (conceptMap && conceptMap.has(slug)) ? conceptMap.get(slug)! : slug;
    return `<a class="flow-sidebar-concept-link" data-link="/concept/${slug}">${display}</a>`;
  });
}

export async function renderFlowView(container: HTMLElement, slug: string): Promise<void | (() => void)> {
  container.innerHTML = `<div class="view"><p class="view__subtitle" style="padding:4rem 2rem;text-align:center;">${translate('common.loading', 'loading...')}</p></div>`;

  let renderer: ReturnType<typeof createFlowRenderer> | null = null;
  let langUnsub: (() => void) | null = null;
  let keyHandler: ((e: KeyboardEvent) => void) | null = null;

  try {
    const [flowData, concept, allConcepts] = await Promise.all([
      api.getFlow(slug) as Promise<FlowData>,
      api.getConcept(slug) as Promise<ApiConceptDetail>,
      api.getConcepts() as Promise<ApiConceptListItem[]>,
    ]);

    // Build slug -> title map for resolving [[slug]] links
    const conceptMap = new Map<string, string>();
    for (const c of allConcepts) {
      const { title: cTitle } = getTranslatedFields(c);
      conceptMap.set(c.slug, cTitle);
    }

    function render(): void {
      if (renderer) { renderer.destroy(); renderer = null; }
      if (keyHandler) { document.removeEventListener('keydown', keyHandler); keyHandler = null; }
      container.innerHTML = '';

      const translatedTitle = getTranslatedFields(concept).title;

      const view = document.createElement('div');
      view.className = 'view view--flow';

      // Header
      const header = document.createElement('div');
      header.className = 'flow-header';
      header.innerHTML = `
        <h1 class="flow-header__title">${translatedTitle}</h1>
        <a class="flow-header__back" data-link="/concept/${slug}">&larr; ${translate('flow.back', 'back to concept')}</a>
      `;
      (header.querySelector('[data-link]') as HTMLElement).addEventListener('click', (e: Event) => {
        e.preventDefault();
        navigate(`/concept/${slug}`);
      });
      view.appendChild(header);

      if (!flowData.steps || flowData.steps.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'not-found';
        empty.style.minHeight = '40vh';
        empty.innerHTML = `<p class="not-found__message">${translate('flow.empty', 'No flow steps defined for this concept.')}</p>`;
        view.appendChild(empty);
        container.appendChild(view);
        return;
      }

      // Graph wrapper with sidebar
      const graphWrapper = document.createElement('div');
      graphWrapper.className = 'flow-graph-wrapper';

      const flowContainer = document.createElement('div');
      flowContainer.className = 'flow-graph-container';
      graphWrapper.appendChild(flowContainer);

      // Sidebar (hidden by default)
      const sidebar = document.createElement('div');
      sidebar.className = 'graph-sidebar';
      sidebar.style.display = 'none';
      sidebar.innerHTML = `
        <button class="graph-sidebar__close">&times;</button>
        <div class="graph-sidebar__content"></div>
      `;
      graphWrapper.appendChild(sidebar);

      // Legend
      const legend = document.createElement('div');
      legend.className = 'flow-legend';
      legend.innerHTML = `
        <span class="flow-legend__item"><span class="flow-legend__key">${translate('legend.click', 'click')}</span> ${translate('flow.legend_step', 'step for details')}</span>
        <span class="flow-legend__item"><span class="flow-legend__key">${translate('legend.drag', 'drag')}</span> ${translate('tree.legend_pan', 'to pan')}</span>
        <span class="flow-legend__item"><span class="flow-legend__key">${translate('legend.scroll', 'scroll')}</span> ${translate('legend.to_zoom', 'to zoom')}</span>
      `;
      flowContainer.appendChild(legend);

      // Shape legend
      const shapeLegend = document.createElement('div');
      shapeLegend.className = 'flow-shape-legend';
      shapeLegend.innerHTML = `
        <span class="flow-shape-legend__item"><span class="flow-shape-legend__oval" style="border-color: #FF4F1A;"></span> ${translate('flow.start', 'start')}</span>
        <span class="flow-shape-legend__item"><span class="flow-shape-legend__rect"></span> ${translate('flow.process', 'process')}</span>
        <span class="flow-shape-legend__item"><span class="flow-shape-legend__diamond" style="border-color: #C8FF3C;"></span> ${translate('flow.decision', 'decision')}</span>
        <span class="flow-shape-legend__item"><span class="flow-shape-legend__oval"></span> ${translate('flow.end', 'end')}</span>
      `;
      flowContainer.appendChild(shapeLegend);

      view.appendChild(graphWrapper);
      container.appendChild(view);

      // Sidebar logic
      const closeSidebar = (): void => {
        sidebar.style.display = 'none';
      };
      (sidebar.querySelector('.graph-sidebar__close') as HTMLButtonElement).addEventListener('click', closeSidebar);

      keyHandler = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') closeSidebar();
      };
      document.addEventListener('keydown', keyHandler);

      const showStepSidebar = (step: FlowStep): void => {
        sidebar.style.display = 'block';
        const content = sidebar.querySelector('.graph-sidebar__content') as HTMLElement;

        const shapeBadge = `<span class="flow-shape-badge flow-shape-badge--${step.shape}">${step.shape}</span>`;

        // Find connected edges
        const outEdges = flowData.edges.filter((e: FlowEdge) => e.from_step_id === step.id);
        const inEdges = flowData.edges.filter((e: FlowEdge) => e.to_step_id === step.id);
        const stepById = (id: number): FlowStep | undefined => flowData.steps.find((s: FlowStep) => s.id === id);

        let connectionsHtml = '';
        if (outEdges.length > 0) {
          connectionsHtml += `<div class="flow-sidebar-section">
            <h4 class="flow-sidebar-section__title">${translate('flow.leads_to', 'Leads to')}</h4>
            ${outEdges.map((e: FlowEdge) => {
              const target = stepById(e.to_step_id);
              return `<div class="flow-sidebar-connection">&rarr; ${target ? renderInlineLinks(target.label, conceptMap) : '?'}${e.label ? ` <span class="flow-sidebar-edge-label">(${e.label})</span>` : ''}</div>`;
            }).join('')}
          </div>`;
        }
        if (inEdges.length > 0) {
          connectionsHtml += `<div class="flow-sidebar-section">
            <h4 class="flow-sidebar-section__title">${translate('flow.comes_from', 'Comes from')}</h4>
            ${inEdges.map((e: FlowEdge) => {
              const source = stepById(e.from_step_id);
              return `<div class="flow-sidebar-connection">&larr; ${source ? renderInlineLinks(source.label, conceptMap) : '?'}${e.label ? ` <span class="flow-sidebar-edge-label">(${e.label})</span>` : ''}</div>`;
            }).join('')}
          </div>`;
        }

        // Concept links
        let linksHtml = '';
        if (step.conceptLinks && step.conceptLinks.length > 0) {
          linksHtml = `<div class="flow-sidebar-section">
            <h4 class="flow-sidebar-section__title">${translate('flow.related_concepts', 'Related concepts')}</h4>
            ${step.conceptLinks.map((link: { slug: string; title: string }) =>
              `<a class="flow-sidebar-concept-link" data-link="/concept/${link.slug}">${link.title}</a>`
            ).join('')}
          </div>`;
        }

        content.innerHTML = `
          <h3 class="graph-sidebar__title">${renderInlineLinks(step.label, conceptMap)} ${shapeBadge}</h3>
          ${connectionsHtml}
          ${linksHtml}
        `;

        // Concept link clicks
        for (const a of content.querySelectorAll('[data-link]')) {
          a.addEventListener('click', (e: Event) => {
            e.preventDefault();
            navigate((a as HTMLElement).dataset.link!);
          });
        }
      };

      renderer = createFlowRenderer(flowContainer, flowData, {
        onStepClick: showStepSidebar,
        conceptMap,
      });
    }

    render();

    langUnsub = subscribe('lang', render);

    return () => {
      if (renderer) renderer.destroy();
      if (langUnsub) langUnsub();
      if (keyHandler) document.removeEventListener('keydown', keyHandler);
    };
  } catch (err) {
    container.innerHTML = `<div class="view"><p class="form-error" style="padding:4rem 2rem;text-align:center;">${translate('flow.error', 'Failed to load flow')}: ${(err as Error).message}</p></div>`;
  }
}
